// Vinculación adicional: el cliente de este requerimiento pasa a compartir un vehículo (con sus equipos GPS) que ya tiene otro cliente.
// Pantalla pensada para quien no conoce el sistema: dice qué se está haciendo, a quién se vincula y qué pasará, y pide confirmar antes de
// vincular (un clic vincula todos los equipos de la placa).
import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, LinearProgress, Paper, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import SearchIcon from "@mui/icons-material/Search";
import SimCardOutlinedIcon from "@mui/icons-material/SimCardOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";

// "Placa" con aspecto de matrícula: se reconoce de un vistazo.
function Matricula({ texto, grande = false }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: grande ? 1 : 0.8,
        py: grande ? 0.2 : 0.1,
        border: "2px solid",
        borderColor: "text.primary",
        borderRadius: "6px",
        bgcolor: "background.paper",
        fontWeight: 800,
        letterSpacing: "0.08em",
        fontSize: grande ? 14.5 : 12.5,
        lineHeight: 1.25,
        whiteSpace: "nowrap",
      }}
    >
      {texto || "—"}
    </Box>
  );
}

// Identificación del equipo: la serie del módem (num_sim) y su IMEI (emai); si son el mismo número se muestra una sola vez.
const idEquipo = (m) => {
  const serie = String(m?.num_sim || "").trim();
  const imei = String(m?.emai || "").trim();
  if (serie && imei && serie !== imei) return `Serie ${serie} · IMEI ${imei}`;
  return `Serie / IMEI ${serie || imei || "—"}`;
};

// Vehículo: el icono arriba y la placa (con aspecto de matrícula) debajo.
function VehiculoConPlaca({ placa }) {
  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", color: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.2 : 0.1) }}>
        <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
      {placa ? <Matricula texto={placa} grande /> : <Chip size="small" label="Sin placa registrada" variant="outlined" />}
    </Box>
  );
}

const nombreChip = (chip) => (typeof chip === "string" ? chip : chip?.numero || chip?.id_historial || "");
const clientesDe = (modem) => (modem.clientes || []).map((c) => c.razon_social || c.cod_cliente || "").filter(Boolean);

function TarjetaVehiculo({ placa, clienteNombre, vinculando, onElegir }) {
  const equipos = placa.modems || [];
  const ocupado = !!vinculando;
  const estaVinculando = vinculando === placa.placa;
  const esAlias = String(placa.placa || "").startsWith("*");
  // Un equipo 'Vendido Activo' no se puede tercerizar: el vehículo se muestra pero no se puede vincular.
  const bloqueada = !!placa.bloqueada;
  const vendidosActivos = equipos.filter((m) => m.estado === "Vendido Activo");
  const duenos = [...new Set(equipos.flatMap(clientesDe))];

  return (
    <Paper
      variant="outlined"
      sx={{ p: 0, overflow: "hidden", display: "flex", flexDirection: "column", borderColor: (t) => (bloqueada ? alpha(t.palette.warning.main, 0.7) : undefined) }}
    >
      {bloqueada && (
        <Alert severity="warning" icon={<LockOutlinedIcon fontSize="small" />} sx={{ borderRadius: 0, py: 0.25, fontSize: 12.5 }}>
          <b>No se puede vincular este vehículo.</b> Tiene {vendidosActivos.length === 1 ? "un equipo" : `${vendidosActivos.length} equipos`} <b>Vendido Activo</b> (
          {vendidosActivos.map((m) => m.num_sim).join(", ")}) y un equipo vendido no se puede tercerizar.
        </Alert>
      )}
      <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", opacity: bloqueada ? 0.85 : 1 }}>
        <VehiculoConPlaca placa={placa.placa} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            {equipos.length} equipo{equipos.length === 1 ? "" : "s"} GPS
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }} noWrap title={duenos.join(", ")}>
            {duenos.length ? `Hoy con: ${duenos.join(", ")}` : "Sin cliente registrado"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 1.5, pb: 1.25, display: "grid", gap: 0.75 }}>
        {equipos.map((modem) => (
          <Box
            key={`${placa.placa}-${modem.num_sim}`}
            sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", p: 0.9, borderRadius: 1.25, bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.07 : 0.045) }}
          >
            <SimCardOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{modem.modelo || "Equipo GPS"}</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {idEquipo(modem)}
                {(modem.chips || []).length > 0 ? ` · Chip ${(modem.chips || []).map(nombreChip).filter(Boolean).join(", ")}` : " · Sin chip"}
              </Typography>
            </Box>
            <Chip
              size="small"
              icon={modem.estado === "Vendido Activo" ? <WarningAmberIcon /> : undefined}
              label={modem.estado || "—"}
              color={modem.estado === "Activo" ? "success" : modem.estado === "Vendido Activo" ? "warning" : "default"}
              variant="outlined"
              sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
            />
          </Box>
        ))}
        {esAlias && (
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
            El "*" delante de la placa es un registro antiguo del mismo vehículo para otro cliente.
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: "auto", p: 1.25, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "flex-end" }}>
        <Tooltip title={bloqueada ? "Un equipo Vendido Activo no se puede tercerizar" : ""}>
          <span>
            <Button
              variant="contained"
              disabled={ocupado || bloqueada}
              onClick={() => onElegir(placa)}
              startIcon={estaVinculando ? <CircularProgress size={16} color="inherit" /> : bloqueada ? <LockOutlinedIcon /> : <LinkIcon />}
              aria-label={bloqueada ? `La placa ${placa.placa} no se puede vincular: tiene un equipo Vendido Activo` : `Vincular la placa ${placa.placa} a ${clienteNombre || "este cliente"}`}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {estaVinculando ? "Vinculando…" : bloqueada ? "No se puede vincular" : "Vincular a este cliente"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
}

// Lo ya vinculado, agrupado por placa: la matrícula arriba y, con líneas, los equipos GPS que tiene esa placa.
const LINEA = 2; // grosor de las líneas del árbol (px)
const sinPlaca = (placa) => !String(placa || "").trim() || !/[A-Za-z0-9]/.test(String(placa));

function agruparPorPlaca(asignados = []) {
  const grupos = new Map();
  for (const item of asignados) {
    const clave = sinPlaca(item.placa) ? "" : String(item.placa).trim();
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push(item);
  }
  return Array.from(grupos, ([placa, equipos]) => ({ placa, equipos }));
}

function EquipoVinculado({ item }) {
  const chips = (item.chips || []).map(nombreChip).filter(Boolean);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", p: 0.9, borderRadius: 1.25, border: 1, borderColor: (t) => alpha(t.palette.success.main, 0.4), bgcolor: (t) => alpha(t.palette.success.main, t.palette.mode === "dark" ? 0.1 : 0.05) }}>
      <SimCardOutlinedIcon sx={{ fontSize: 18, color: "success.main" }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>{item.modelo || "Equipo GPS"}</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {idEquipo(item)}
          {chips.length > 0 ? ` · Chip ${chips.join(", ")}` : " · Sin chip"}
        </Typography>
      </Box>
      <Chip size="small" icon={<LinkIcon />} color="success" variant="outlined" label="Vinculado" sx={{ height: 22, fontWeight: 700 }} />
    </Box>
  );
}

// Cuadrícula de dos columnas: a la izquierda el vehículo (icono + placa) y, debajo, el tronco de la línea que sale del CENTRO de la placa;
// a la derecha el conteo y, en cada fila, el equipo al que llega una rama horizontal del tronco.
function ArbolVinculados({ asignados }) {
  const grupos = useMemo(() => agruparPorPlaca(asignados), [asignados]);
  const linea = { content: '""', position: "absolute", borderColor: "divider", borderStyle: "solid", borderWidth: 0 };
  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {grupos.map((g) => (
        <Box key={g.placa || "sin-placa"} sx={{ display: "grid", gridTemplateColumns: "minmax(120px, auto) minmax(0, 1fr)", alignItems: "stretch" }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <VehiculoConPlaca placa={g.placa} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-end", pl: 1.5, pb: 0.5 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {g.equipos.length} equipo{g.equipos.length === 1 ? "" : "s"} GPS vinculado{g.equipos.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          {g.equipos.map((item, i) => {
            const ultimo = i === g.equipos.length - 1;
            return (
              <React.Fragment key={item.id_historial || item.num_sim}>
                <Box
                  sx={{
                    position: "relative",
                    minHeight: 24,
                    "&::before": { ...linea, left: "50%", top: 0, height: ultimo ? "50%" : "100%", borderLeftWidth: LINEA },
                    "&::after": { ...linea, left: "50%", right: 0, top: "50%", borderTopWidth: LINEA },
                  }}
                />
                <Box sx={{ py: 0.5, pl: 0 }}>
                  <EquipoVinculado item={item} />
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

function Vacio({ icono, titulo, texto, accion }) {
  return (
    <Box sx={{ py: 4, px: 2, textAlign: "center", display: "grid", justifyItems: "center", gap: 0.75 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", display: "grid", placeItems: "center", color: "text.secondary", bgcolor: (t) => alpha(t.palette.text.primary, 0.07) }}>{icono}</Box>
      <Typography sx={{ fontWeight: 700 }}>{titulo}</Typography>
      <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 520 }}>{texto}</Typography>
      {accion}
    </Box>
  );
}

export default function VinculacionAdicionalSection({
  q, setQ, placas, placasRequerimiento = [], sinPlacasRequerimiento = false, loading, onRefresh, onVincularPlaca, vinculando,
  asignados, asignadosLoading, clienteNombre = "", servicioDescripcion = "", onEditarCoordinacion,
}) {
  const [elegida, setElegida] = useState(null);
  const totalEquipos = useMemo(() => placas.reduce((a, p) => a + (Array.isArray(p?.modems) ? p.modems.length : 0), 0), [placas]);
  const placasOrdenadas = useMemo(() => [...placas].sort((a, b) => Number(!!a.bloqueada) - Number(!!b.bloqueada)), [placas]); // las vinculables primero
  const totalBloqueadas = useMemo(() => placas.filter((p) => p.bloqueada).length, [placas]);
  const cliente = clienteNombre || "este cliente";
  const hayVinculados = (asignados?.length ?? 0) > 0;

  const confirmar = async () => {
    const placa = elegida;
    setElegida(null);
    await onVincularPlaca?.(placa);
  };

  return (
    <Stack spacing={2}>
      {/* Qué se hace aquí, en una frase */}
      <Paper variant="outlined" sx={{ p: 1.75, display: "flex", gap: 1.5, alignItems: "flex-start", borderColor: (t) => alpha(t.palette.info.main, 0.5), bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === "dark" ? 0.1 : 0.05) }}>
        <PeopleOutlineIcon color="info" sx={{ mt: 0.25 }} />
        <Box>
          <Typography sx={{ fontWeight: 800 }}>Vincular un vehículo que ya tiene GPS con otro cliente</Typography>
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.25 }}>
            <b>{cliente}</b> va a compartir el vehículo y sus equipos con quien ya los tiene. Los equipos <b>no se mueven ni se duplican</b>:
            solo quedan disponibles para los dos clientes.
          </Typography>
          {servicioDescripcion && <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>Servicio de este requerimiento: {servicioDescripcion}</Typography>}
        </Box>
      </Paper>

      {/* Paso 1: elegir el vehículo */}
      <Paper variant="outlined" sx={{ p: 1.75 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1, mb: 1.25 }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontWeight: 800 }}>1. Elige el vehículo</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.25 }}>
              {placasRequerimiento.length > 0 ? (
                <>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Placas de este requerimiento:</Typography>
                  {placasRequerimiento.map((p) => <Matricula key={p} texto={p} />)}
                </>
              ) : (
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Busca el vehículo por placa, serie del equipo o cliente.</Typography>
              )}
            </Stack>
          </Box>
          <TextField
            size="small" placeholder="Buscar placa, serie o cliente…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: { xs: "100%", sm: 280 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          <Button size="small" onClick={onRefresh} startIcon={<ReplayIcon />} sx={{ textTransform: "none" }}>Actualizar</Button>
        </Stack>

        {loading ? (
          <Box sx={{ py: 2 }}><LinearProgress /></Box>
        ) : placas.length === 0 ? (
          sinPlacasRequerimiento && !q.trim() ? (
            <Vacio
              icono={<DirectionsCarFilledOutlinedIcon />}
              titulo="Este requerimiento todavía no tiene placa"
              texto="Registra la placa del vehículo en la coordinación para ver sus equipos aquí. También puedes buscar el vehículo con el cuadro de arriba."
              accion={onEditarCoordinacion && <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={onEditarCoordinacion} sx={{ mt: 0.5, textTransform: "none" }}>Editar coordinación</Button>}
            />
          ) : (
            <Vacio
              icono={<SearchIcon />}
              titulo={placasRequerimiento.length ? "No hay equipos para vincular en esas placas" : "No encontramos ese vehículo"}
              texto={
                placasRequerimiento.length
                  ? "Puede ser que la placa esté mal escrita, que el vehículo no tenga equipos con otro cliente o que sus equipos estén vendidos (esos no se vinculan). Revisa la placa en la coordinación."
                  : "Prueba con otra placa, o con la serie del equipo o el nombre del cliente."
              }
              accion={placasRequerimiento.length > 0 && onEditarCoordinacion && <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={onEditarCoordinacion} sx={{ mt: 0.5, textTransform: "none" }}>Revisar placa en la coordinación</Button>}
            />
          )
        ) : (
          <>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1 }}>
              {placas.length} vehículo{placas.length === 1 ? "" : "s"} · {totalEquipos} equipo{totalEquipos === 1 ? "" : "s"}
              {totalBloqueadas > 0 && ` · ${totalBloqueadas} no se puede${totalBloqueadas === 1 ? "" : "n"} vincular (tiene${totalBloqueadas === 1 ? "" : "n"} un equipo Vendido Activo)`}. Al vincular un vehículo se vinculan todos sus equipos.
            </Typography>
            <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}>
              {placasOrdenadas.map((p) => <TarjetaVehiculo key={p.id_vehiculo || p.placa} placa={p} clienteNombre={cliente} vinculando={vinculando} onElegir={setElegida} />)}
            </Box>
          </>
        )}
      </Paper>

      {/* Resultado */}
      <Paper variant="outlined" sx={{ p: 1.75, borderColor: (t) => (hayVinculados ? alpha(t.palette.success.main, 0.55) : undefined) }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: hayVinculados ? 1 : 0.25 }}>
          <CheckCircleOutlineIcon color={hayVinculados ? "success" : "disabled"} />
          <Typography sx={{ fontWeight: 800 }}>2. Ya vinculados a {cliente}</Typography>
          {hayVinculados && <Chip size="small" color="success" label={`${asignados.length} equipo${asignados.length === 1 ? "" : "s"}`} sx={{ fontWeight: 700, height: 20 }} />}
        </Stack>
        {asignadosLoading ? (
          <LinearProgress />
        ) : !hayVinculados ? (
          <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>Todavía no vinculaste ningún equipo. Cuando lo hagas aparecerá aquí, y podrás continuar con el trabajo de campo.</Typography>
        ) : (
          <>
          <ArbolVinculados asignados={asignados} />
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1.25 }}>
            Cuando termines de vincular, pulsa <b>Siguiente</b> (abajo a la derecha) para pasar al trabajo de campo.
          </Typography>
          </>
        )}
      </Paper>

      {/* Confirmación antes de vincular */}
      <Dialog open={!!elegida} onClose={() => setElegida(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>¿Vincular este vehículo?</DialogTitle>
        <DialogContent>
          {elegida && (
            <Stack spacing={1.25}>
              <Box><Matricula texto={elegida.placa} grande /></Box>
              <Typography sx={{ fontSize: 14 }}>
                Se vinculará a <b>{cliente}</b>. Quedarán vinculados estos <b>{(elegida.modems || []).length} equipo{(elegida.modems || []).length === 1 ? "" : "s"}</b>:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25, display: "grid", gap: 0.4 }}>
                {(elegida.modems || []).map((m) => (
                  <li key={m.num_sim}>
                    <Typography sx={{ fontSize: 13.5 }}>
                      {m.modelo || "Equipo GPS"} · {idEquipo(m)}
                      {clientesDe(m).length > 0 && <Typography component="span" sx={{ color: "text.secondary", fontSize: 12.5 }}>{` (hoy con ${clientesDe(m).join(", ")})`}</Typography>}
                    </Typography>
                  </li>
                ))}
              </Box>
              <Alert severity="info" icon={false} sx={{ py: 0.25, fontSize: 12.5 }}>
                Los equipos siguen con su cliente actual: solo se agrega a {cliente}.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setElegida(null)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" startIcon={<LinkIcon />} onClick={confirmar} autoFocus sx={{ textTransform: "none", fontWeight: 700 }}>Sí, vincular</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
